import React, { useEffect, useRef, useState } from "react";
import { Button, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useReactiveVar } from "@apollo/client";

import useEffectiveLanguage from "components/use-hooks/use-effective-language";
import useTranslatedText from "components/use-hooks/use-translated-text";
import { localizeIt } from "components/service/localize";
import TranslateLanguageIcon from "components/layout/icons/translate-language-icon";
import { translationParamsVar } from "graphql/cache";
import { TRANSLATION_FIELD_TYPES } from "mock-data/translation/languages";

import { requestTextTranslation } from "../dynamic-translation";
import { hasTranslationApiAccess } from "../translation-ai-client";
import { detectGermanEnglishTextLanguage } from "../translation-language-detection";
import { getOptionalTranslationDirection } from "../translation-optional-direction";

import "./translated-text.scss";

const toggleLanguages = [ "en", "de" ];
const toggleNamespaces = [
    TRANSLATION_FIELD_TYPES.comment,
    TRANSLATION_FIELD_TYPES.jobDescription
];

const TranslatedText = ({
        text,
        fallback = "",
        namespace,
        context,
        sourceLang
    }) => {
    const value = text || "";
    const { language } = useEffectiveLanguage();
    const translationParams = useReactiveVar(translationParamsVar);
    const [ viewMode, setViewMode ] = useState("source");
    const [ manualTranslatedText, setManualTranslatedText ] = useState("");
    const [ loading, setLoading ] = useState(false);
    const mountedRef = useRef(true);
    const cancelRef = useRef(null);
    const canToggleNamespace = toggleNamespaces.indexOf(namespace) !== -1;
    const detectedLanguage = toggleLanguages.indexOf(language) !== -1 ?
        detectGermanEnglishTextLanguage(value) :
        null;
    const translatedText = useTranslatedText(text, {
        namespace,
        context,
        sourceLang
    });
    const canToggle = canToggleNamespace &&
        toggleLanguages.indexOf(language) !== -1 &&
        detectedLanguage !== language &&
        hasTranslationApiAccess(translationParams) &&
        !!value.trim();
    const displayText = viewMode === "translated" ?
        manualTranslatedText :
        value;
    const tooltipText = viewMode === "translated" ?
        localizeIt("TRANSLATION.Button_Text_ShowOriginal") :
        localizeIt("TRANSLATION.Button_Text_Translate");

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;

            if (cancelRef.current) {
                cancelRef.current();
                cancelRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        setViewMode("source");
        setManualTranslatedText("");
        setLoading(false);

        if (cancelRef.current) {
            cancelRef.current();
            cancelRef.current = null;
        }
    }, [ value, language, namespace, context ]);

    const handleToggle = () => {
        if (!canToggle || loading) return;

        if (viewMode === "translated") {
            setViewMode("source");
            return;
        }

        if (manualTranslatedText) {
            setViewMode("translated");
            return;
        }

        setLoading(true);

        if (cancelRef.current) {
            cancelRef.current();
            cancelRef.current = null;
        }

        const direction = getOptionalTranslationDirection({
                language,
                viewMode     : "de",
                textLanguage : detectedLanguage
        });

        cancelRef.current = requestTextTranslation({
            sourceLang : direction.sourceLang,
            targetLang : direction.targetLang,
            namespace,
            context,
            text       : value
        }, (translatedValue) => {
            if (!mountedRef.current) return;

            cancelRef.current = null;
            setManualTranslatedText(translatedValue);
            setViewMode("translated");
            setLoading(false);
        });
    };

    if (!canToggle) return <>{ translatedText || fallback }</>;

    return (
        <span className="translated-text">
            <span>{ displayText || fallback }</span>
            <Tooltip title={ tooltipText }>
                <Button
                    type="text"
                    className={ `translated-text-button ${ loading ? "is-loading" : "" }` }
                    onClick={ handleToggle }
                    aria-label={ tooltipText }
                    aria-busy={ loading }
                    aria-disabled={ loading }
                >
                    { loading ? <LoadingOutlined spin /> : <TranslateLanguageIcon /> }
                </Button>
            </Tooltip>
        </span>
    );
};

export default TranslatedText;
